import React, {useCallback, useEffect, useState} from 'react';
import createAxiosInstance from "../support/AxiosSupport";
import Spinner from "./Spinner";

/** Одна KPI-метрика дашборда (контракт backend `GET /api/admin/kpi`, CTHS-37). */
interface KpiMetric {
    key: string;
    label: string;
    value: number | null;
    target: number | null;
    prevValue: number | null;
    achieved: boolean;
    unit: string;
}

/** Acceptance одного типа рекомендаций за период (CTHS-328). `rate: null` — решений не было. */
interface AcceptanceByType {
    type: string;
    label: string;
    accepted: number;
    dismissed: number;
    skipped: number;
    total: number;
    rate: number | null;
}

interface KpiDashboardData {
    metrics: KpiMetric[];
    periodDays: number;
    /** Может отсутствовать в ответе старого бэкенда — секция тогда не рисуется. */
    acceptanceByType?: AcceptanceByType[];
}

/** Допустимые периоды дашборда (дни) — синхронно с backend-валидацией 7/30/90 (AC-2). */
const PERIODS = [7, 30, 90] as const;
type Period = typeof PERIODS[number];

/** Форматирует значение метрики с учётом единицы измерения; `null` → «–» (AC-1/AC-4). */
const formatValue = (value: number | null, unit: string): string => {
    if (value === null || value === undefined) {
        return '–';
    }
    switch (unit) {
        case 'PERCENT':
            return `${Math.round(value)}%`;
        case 'DAYS':
            return `${Math.round(value * 10) / 10} дн`;
        default:
            return String(Math.round(value));
    }
};

/** Целевой acceptance rate рекомендаций из vision.md §success-metrics (NSM). */
const ACCEPTANCE_TARGET = 25;

/** Доля принятых; `null` (решений за период не было) — «—», а не «0 %». */
const formatRate = (rate: number | null): string =>
    (rate === null || rate === undefined ? '—' : `${Math.round(rate)}%`);

/** Цвет доли: без данных — нейтральный, иначе сравнение с целевым порогом NSM. */
const acceptanceColor = (rate: number | null): string => {
    if (rate === null || rate === undefined) {
        return 'text-gray-400 dark:text-gray-500';
    }
    return rate >= ACCEPTANCE_TARGET
        ? 'text-green-600 dark:text-green-400'
        : 'text-red-500 dark:text-red-400';
};

/** Стрелка тренда относительно предыдущего периода (тонкий визуальный индикатор). */
const trendSymbol = (value: number | null, prevValue: number | null): string => {
    if (value === null || prevValue === null || value === undefined || prevValue === undefined) {
        return '';
    }
    if (value > prevValue) {
        return '▲';
    }
    if (value < prevValue) {
        return '▼';
    }
    return '→';
};

/**
 * Карточка одной KPI-метрики: значение, цель и цветовой статус достижения. Цвета — через
 * Tailwind-токены light/dark (C-6.1), без raw hex. `null`-значение рендерится как «–» (AC-1).
 */
const KpiMetricCard: React.FC<{ metric: KpiMetric }> = ({metric}) => {
    const hasValue = metric.value !== null && metric.value !== undefined;
    const statusColor = !hasValue
        ? 'text-gray-400 dark:text-gray-500'
        : metric.achieved
            ? 'text-green-600 dark:text-green-400'
            : 'text-red-500 dark:text-red-400';
    const trend = trendSymbol(metric.value, metric.prevValue);

    return (
        <div className="card p-4" data-testid={`kpi-card-${metric.key}`}>
            <div className="flex items-baseline justify-between">
                <span className={`text-2xl font-bold ${statusColor}`} data-testid={`kpi-value-${metric.key}`}>
                    {formatValue(metric.value, metric.unit)}
                </span>
                {trend && (
                    <span className="text-sm text-gray-400 dark:text-gray-500 ml-2"
                          data-testid={`kpi-trend-${metric.key}`}>
                        {trend}
                    </span>
                )}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">{metric.label}</div>
            <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                Цель: {formatValue(metric.target, metric.unit)}
            </div>
        </div>
    );
};

/**
 * Таблица acceptance по типам рекомендаций (CTHS-328): объём решений и доля принятых по каждому
 * типу ленты. Тип без решений показывает «—», а не 0 %: отсутствие сигнала — это не провал цели,
 * и красить его в красный значит врать администратору (FR-24).
 */
const AcceptanceByTypeTable: React.FC<{rows: AcceptanceByType[]}> = ({rows}) => (
    <div className="space-y-2" data-testid="kpi-acceptance-by-type">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Acceptance по типам рекомендаций
        </h3>
        <div className="card overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        <th scope="col" className="px-4 py-3 font-medium">Тип</th>
                        <th scope="col" className="px-4 py-3 font-medium text-right">Принято</th>
                        <th scope="col" className="px-4 py-3 font-medium text-right">Отклонено</th>
                        <th scope="col" className="px-4 py-3 font-medium text-right">Всего</th>
                        <th scope="col" className="px-4 py-3 font-medium text-right">Acceptance</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.type} data-testid={`kpi-acceptance-row-${row.type}`}
                            className="border-t border-gray-100 dark:border-white/5">
                            <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{row.label}</td>
                            <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-300">{row.accepted}</td>
                            <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-300">{row.dismissed}</td>
                            <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-300">{row.total}</td>
                            <td className={`px-4 py-3 text-right font-medium ${acceptanceColor(row.rate)}`}
                                data-testid={`kpi-acceptance-rate-${row.type}`}>
                                {formatRate(row.rate)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
);

/**
 * Раздел «Продуктовые KPI» панели администратора (CTHS-37): NSM и метрики vision с целями и
 * трендом. Переключатель периода 7d/30d/90d (AC-2), состояния loading/empty/error (AC-4, C-6.3),
 * работает в light/dark без хардкода цветов (AC-5). Доступ ограничен ролью `clothes-admin` на
 * уровне backend-эндпоинта (AC-3).
 */
const KpiDashboard: React.FC = () => {
    const [period, setPeriod] = useState<Period>(30);
    const [data, setData] = useState<KpiDashboardData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const api = await createAxiosInstance();
            const res = await api.get<KpiDashboardData>('/admin/kpi', {params: {period}});
            setData(res.data);
        } catch (e) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [period]);

    useEffect(() => {
        load();
    }, [load]);

    const periodSwitcher = (
        <div className="flex space-x-2" data-testid="kpi-period-switcher">
            {PERIODS.map((p) => (
                <button
                    key={p}
                    onClick={() => setPeriod(p)}
                    className={`px-3 py-1 rounded-lg text-sm font-medium ${
                        period === p
                            ? 'bg-brand-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                    data-testid={`kpi-period-${p}`}
                >
                    {p}d
                </button>
            ))}
        </div>
    );

    if (loading) {
        return <Spinner/>;
    }

    if (error) {
        return (
            <div className="p-6 text-center" data-testid="kpi-error">
                <p className="text-gray-600 dark:text-gray-300 mb-4">Не удалось загрузить KPI</p>
                <button
                    onClick={load}
                    className="px-4 py-2 rounded-xl bg-brand-600 text-white hover:bg-brand-700"
                >
                    Повторить
                </button>
            </div>
        );
    }

    const metrics = data?.metrics ?? [];
    const acceptanceByType = data?.acceptanceByType ?? [];

    return (
        <div className="space-y-4" data-testid="kpi-dashboard">
            <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Продуктовые KPI</h2>
                {periodSwitcher}
            </div>

            {metrics.length === 0 ? (
                <div className="p-6 text-center text-gray-500 dark:text-gray-400" data-testid="kpi-empty">
                    Нет данных за выбранный период
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="kpi-grid">
                    {metrics.map((m) => (
                        <KpiMetricCard key={m.key} metric={m}/>
                    ))}
                </div>
            )}

            {acceptanceByType.length > 0 && <AcceptanceByTypeTable rows={acceptanceByType}/>}
        </div>
    );
};

export default KpiDashboard;
